import React, { useEffect, useState } from 'react';
import { onboardingAPI } from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { UserPlus, MagnifyingGlass, Users, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import PacketFormsSection from './PacketFormsSection';
import ApplicantDetailDialog, { STATUS_LABELS, STATUS_STYLES } from './ApplicantDetailDialog';

const EMPTY = { first_name: '', last_name: '', email: '', phone: '', notes: '', send_email: true };

const OnboardingTab = () => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    try { const res = await onboardingAPI.listApplicants(); setApplicants(res.data); }
    catch { toast.error('Failed to load applicants'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) { toast.error('First name, last name and email are required'); return; }
    setSaving(true);
    try {
      const res = await onboardingAPI.createApplicant(form);
      const r = res.data.email_result;
      if (r === 'sent') toast.success(`Welcome email sent to ${form.email}`);
      else if (r && r.startsWith('failed')) toast.warning(`Applicant created, but email ${r}. Use "Send Welcome Email" to retry.`);
      else toast.success('Potential caregiver added');
      setIsCreateOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create applicant'); }
    finally { setSaving(false); }
  };

  const counts = applicants.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {});
  const visible = applicants.filter(a => {
    if (filter === 'active' && a.status === 'hired') return false;
    if (filter !== 'active' && filter !== 'all' && a.status !== filter) return false;
    const q = search.toLowerCase();
    return !q || `${a.first_name} ${a.last_name} ${a.email} ${a.phone || ''}`.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="onboarding-tab">
      <Tabs defaultValue="applicants" className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <TabsList className="rounded-xl">
            <TabsTrigger value="applicants" className="rounded-lg gap-1.5" data-testid="subtab-applicants"><Users className="w-4 h-4" /> Potential Caregivers <Badge variant="secondary" className="rounded-full text-[10px] ml-1">{applicants.filter(a => a.status !== 'hired').length}</Badge></TabsTrigger>
            <TabsTrigger value="forms" className="rounded-lg gap-1.5" data-testid="subtab-forms"><FilePdf className="w-4 h-4" /> Packet Forms</TabsTrigger>
          </TabsList>
          <Button className="rounded-xl gap-2" onClick={() => setIsCreateOpen(true)} data-testid="add-applicant-btn"><UserPlus weight="bold" className="w-4 h-4" /> Add Potential Caregiver</Button>
        </div>

        <TabsContent value="applicants" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {['new', 'invited', 'in_progress', 'completed', 'hired'].map(s => (
              <button key={s} onClick={() => setFilter(filter === s ? 'active' : s)} className={`rounded-2xl border p-3 text-left transition-colors ${filter === s ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/40'}`} data-testid={`status-filter-${s}`}>
                <p className="text-2xl font-heading font-semibold tabular-nums">{counts[s] || 0}</p>
                <p className="text-xs text-muted-foreground">{STATUS_LABELS[s]}</p>
              </button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search applicants…" className="pl-9 rounded-xl" data-testid="applicant-search" />
          </div>

          {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div> : visible.length === 0 ? (
            <Card className="rounded-2xl border-dashed"><CardContent className="py-12 flex flex-col items-center text-center">
              <UserPlus weight="duotone" className="w-14 h-14 text-muted-foreground/50 mb-3" />
              <h3 className="font-heading text-lg font-medium">{applicants.length ? 'No matching applicants' : 'No potential caregivers yet'}</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">Add a potential caregiver and they'll receive a welcome email with a secure link to fill out and sign the onboarding packet.</p>
            </CardContent></Card>
          ) : (
            <Card className="rounded-2xl overflow-hidden">
              <div className="divide-y divide-border">
                {visible.map(a => {
                  const total = a.total_forms + (a.required_docs || 0);
                  const doneCount = a.completed_forms + (a.completed_required_docs || 0);
                  const pct = total ? Math.round((doneCount / total) * 100) : 0;
                  return (
                    <button key={a.id} onClick={() => setDetailId(a.id)} className="w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors" data-testid={`applicant-row-${a.id}`}>
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">{a.first_name[0]}{a.last_name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{a.first_name} {a.last_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.email}{a.phone ? ` · ${a.phone}` : ''}</p>
                      </div>
                      <div className="hidden sm:block w-44">
                        <div className="flex justify-between text-[11px] text-muted-foreground mb-1"><span>{a.completed_forms}/{a.total_forms} forms{a.required_docs ? ` · ${a.completed_required_docs}/${a.required_docs} docs` : ''}</span><span>{pct}%</span></div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                      </div>
                      <div className="text-right shrink-0">
                        <Badge className={`rounded-full ${STATUS_STYLES[a.status]}`}>{STATUS_LABELS[a.status]}</Badge>
                        <p className="text-[11px] text-muted-foreground mt-1">{a.invited_at ? `Invited ${format(new Date(a.invited_at), 'MMM d')}` : `Added ${format(new Date(a.created_at), 'MMM d')}`}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="forms"><PacketFormsSection onChanged={load} /></TabsContent>
      </Tabs>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-applicant-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Potential Caregiver</DialogTitle>
            <DialogDescription>They'll get a welcome email with a secure link to complete the onboarding packet.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>First name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl" data-testid="applicant-first-name" /></div>
              <div className="space-y-1.5"><Label>Last name *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl" data-testid="applicant-last-name" /></div>
            </div>
            <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" data-testid="applicant-email" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" data-testid="applicant-phone" /></div>
            <div className="space-y-1.5"><Label>Internal notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" placeholder="Referred by…, interview date…" /></div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: !!v })} data-testid="applicant-send-email" /> Send welcome email with forms now</label>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" disabled={saving} data-testid="create-applicant-submit">{saving ? 'Saving…' : 'Add Caregiver'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ApplicantDetailDialog applicantId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
};

export default OnboardingTab;
